import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';

import { api } from '@/api';
import { Option } from '@/common/types';
import { API_URL } from '@/dtos';
import { ITriggerDto } from '@/dtos/models/ITriggerDto';
import { useCurrentOrganization } from '@/modules/core/hooks';
import { TriggerCategory, TriggerCategoryType } from '@/modules/HealthPlan/HealthPlan.types.ts';

export const QUERY_KEY = 'GET_TRIGGERS_LIST_V2';

const DEFAULT_PAGE_SIZE = 300;
const SORT = 'id,DESC';

interface PaginatedResponse {
  content: (ITriggerDto & { description?: string })[];
  totalPages: number;
  number: number;
}

const useGetTriggers = (type: TriggerCategoryType = TriggerCategory.FAMILY, page = 0) => {
  const { observingOrganization } = useCurrentOrganization();
  const organizationPath = observingOrganization?.path;

  const { data, isLoading } = useQuery(
    [QUERY_KEY, organizationPath, type, page],
    () =>
      api.get<PaginatedResponse>(API_URL.TRIGGERS, {
        params: {
          organization_path: organizationPath,
          type,
          size: DEFAULT_PAGE_SIZE,
          page,
          sort: SORT,
        },
      }),
    { enabled: !!organizationPath }
  );

  const fieldOptions = useMemo<Option[]>(() => {
    const list = data?.data?.content || [];
    return list.map((item) => ({
      key: `${item.id}`,
      value: item.name || '',
      title: item.description || item.name || '',
    }));
  }, [data]);

  const totalPages = data?.data?.totalPages || 0;
  const currentPage = data?.data?.number || 0;

  return {
    isLoading,
    fieldOptions,
    page: currentPage,
    totalPages,
  };
};

export default useGetTriggers;
